import { useQuery } from "@tanstack/react-query";
import { Calculator, Download, Lock, LockOpen, RefreshCw, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  calcularTbfin,
  cancelarEnvioTbfin,
  confirmarTbfin,
  descargarArchivoTbfin,
  editarFolioTbfin,
  enviarCenaceTbfin,
  enviarTbfin,
  getBitacoraTbfin,
  getContratos,
  reabrirTbfin,
  syncEstatusCenace,
  type TbfinResultado,
} from "@/lib/api";
import { usePermisos } from "@/lib/authz";
import { formatFecha } from "@/lib/utils";

function n(v: number, d = 2): string {
  return v.toLocaleString("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d });
}

const EVENTO_LABEL: Record<string, { txt: string; cls: string }> = {
  confirmado: { txt: "Confirmado", cls: "bg-blue-100 text-blue-700" },
  reabierto: { txt: "Reabierto", cls: "bg-slate-100 text-slate-700" },
  enviado_manual: { txt: "Marcado enviado (manual)", cls: "bg-emerald-100 text-emerald-700" },
  envio_lanzado: { txt: "Envío a CENACE lanzado", cls: "bg-sky-100 text-sky-700" },
  envio_reintento: { txt: "Reintento de envío (timeout del SIM)", cls: "bg-amber-100 text-amber-700" },
  enviado: { txt: "Enviado a CENACE", cls: "bg-emerald-100 text-emerald-700" },
  enviado_detectado: { txt: "Detectado en SIM (registro manual)", cls: "bg-teal-100 text-teal-700" },
  envio_fallido: { txt: "Envío falló", cls: "bg-red-100 text-red-700" },
  cancelado_envio: { txt: "Envío cancelado", cls: "bg-amber-100 text-amber-700" },
  estatus_cenace: { txt: "Estatus CENACE", cls: "bg-sky-100 text-sky-700" },
  sin_folio_cenace: { txt: "Folio CENACE no asignado", cls: "bg-red-100 text-red-700" },
  folio_editado: { txt: "Folio editado", cls: "bg-slate-100 text-slate-700" },
};

export function TbfinPage() {
  const { has } = usePermisos();
  const [searchParams] = useSearchParams();
  // Recuerda la última selección entre navegaciones/recargas (localStorage),
  // dándole prioridad a lo que venga por la URL (?contrato=&fecha=).
  const [contrato, setContrato] = useState<string>(
    () => searchParams.get("contrato") ?? localStorage.getItem("tbfin.contrato") ?? "",
  );
  const [fecha, setFecha] = useState<string>(
    () => searchParams.get("fecha") ?? localStorage.getItem("tbfin.fecha") ?? "2026-06-06",
  );
  const [res, setRes] = useState<TbfinResultado | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const contratosQ = useQuery({ queryKey: ["contratos"], queryFn: getContratos });
  // Solo contratos de ENERGÍA: el TBFin no aplica a las fichas de potencia (no
  // tienen nodo PML; el suyo es el placeholder 'SIN'). Incluye híbridos (RIC).
  const contratos = (contratosQ.data?.items ?? []).filter((c) =>
    c.productos?.includes("Energía"),
  );

  const bitacoraQ = useQuery({
    queryKey: ["bitacora", contrato, fecha],
    queryFn: () => getBitacoraTbfin(contrato, fecha),
    enabled: !!contrato && !!fecha,
  });

  useEffect(() => {
    if (!contratos.length) return;
    // Si no hay selección, o la guardada ya no existe, cae al primer contrato.
    const existe = contratos.some((c) => String(c.id_contrato) === contrato);
    if (!contrato || !existe) setContrato(String(contratos[0].id_contrato));
  }, [contratos, contrato]);

  // Persiste contrato/fecha para recordarlos la próxima vez que se abra la pantalla.
  useEffect(() => {
    if (contrato) localStorage.setItem("tbfin.contrato", contrato);
  }, [contrato]);
  useEffect(() => {
    if (fecha) localStorage.setItem("tbfin.fecha", fecha);
  }, [fecha]);

  const calcular = async () => {
    if (!contrato || !fecha) return;
    setLoading(true);
    setError(null);
    try {
      setRes(await calcularTbfin({ contrato, fecha }));
    } catch (e) {
      setError((e as Error).message);
      setRes(null);
    } finally {
      setLoading(false);
    }
  };

  // Calcula automáticamente cuando cambian contrato / fecha.
  useEffect(() => {
    if (contrato && fecha) void calcular();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contrato, fecha]);

  const [accion, setAccion] = useState(false);

  const confirmar = async () => {
    if (!contrato || !fecha) return;
    setAccion(true);
    setError(null);
    try {
      setRes(await confirmarTbfin(contrato, fecha));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAccion(false);
    }
  };

  const reabrir = async () => {
    if (!contrato || !fecha) return;
    setAccion(true);
    setError(null);
    try {
      setRes(await reabrirTbfin(contrato, fecha));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAccion(false);
    }
  };

  const enviar = async () => {
    if (!contrato || !fecha) return;
    setAccion(true);
    setError(null);
    try {
      setRes(await enviarTbfin(contrato, fecha));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAccion(false);
    }
  };

  // ENVÍO REAL automatizado (dispara el Cloud Run Job).
  const enviarCenace = async () => {
    if (!contrato || !fecha) return;
    const ok = window.confirm(
      `Vas a ENVIAR Y REGISTRAR en CENACE (PRODUCCIÓN) la TBFin del ${fecha}.` +
        "\n\nEs una acción real y vinculante. El robot iniciará sesión con tu FIEL, " +
        "llenará el SIM y hará clic en «Registrar». ¿Continuar?",
    );
    if (!ok) return;
    setAccion(true);
    setError(null);
    try {
      setRes(await enviarCenaceTbfin(contrato, fecha));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAccion(false);
    }
  };

  const puedeEditarFolio = has("editar_folio");
  const [folioEdit, setFolioEdit] = useState("");
  useEffect(() => {
    setFolioEdit(res?.folio_cenace ?? "");
  }, [res?.folio_cenace]);
  const guardarFolio = async () => {
    if (!contrato || !fecha) return;
    setAccion(true);
    setError(null);
    try {
      setRes(await editarFolioTbfin(contrato, fecha, folioEdit.trim()));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAccion(false);
    }
  };

  const [avisoSync, setAvisoSync] = useState<string | null>(null);
  const sincronizarEstatus = async () => {
    setAccion(true);
    setError(null);
    setAvisoSync(null);
    try {
      const r = await syncEstatusCenace();
      setAvisoSync(r.mensaje);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAccion(false);
    }
  };

  const cancelarEnvio = async () => {
    if (!contrato || !fecha) return;
    const ok = window.confirm(
      "Vas a CANCELAR el envío de esta TBFin para poder reenviarla.\n\n" +
        "OJO: esto NO cancela el registro ya hecho en CENACE — solo reabre el flujo aquí. " +
        "Si ya se registró en el SIM, reenviar duplicaría salvo que lo canceles antes en CENACE. " +
        "¿Continuar?",
    );
    if (!ok) return;
    setAccion(true);
    setError(null);
    try {
      setRes(await cancelarEnvioTbfin(contrato, fecha));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAccion(false);
    }
  };

  // Mientras el Job corre (estado 'enviando'), refrescar hasta que cambie.
  useEffect(() => {
    if (res?.estado !== "enviando" || !contrato || !fecha) return;
    const id = setInterval(async () => {
      try {
        const r = await calcularTbfin({ contrato, fecha });
        setRes(r);
        if (r.estado !== "enviando") clearInterval(id);
      } catch {
        /* reintenta en el próximo tick */
      }
    }, 5000);
    return () => clearInterval(id);
  }, [res?.estado, contrato, fecha]);

  // Refrescar el historial tras cada acción (confirmar/enviar/cancelar/reabrir).
  useEffect(() => {
    if (contrato && fecha) void bitacoraQ.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [res]);

  const descargar = async () => {
    if (!contrato || !fecha) return;
    setError(null);
    try {
      await descargarArchivoTbfin(contrato, fecha);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const confirmado = res?.estado === "confirmado" || res?.estado === "enviado";

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Cálculo TBFin</h1>
        <p className="text-sm text-slate-500 mt-1">
          Activación horaria y MWh a programar en el MDA. Usa el snapshot de parámetros vigente,
          TReg del año, HSC/CPI/TC y el PML del nodo.
        </p>
      </div>

      <div className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Contrato</span>
          <select
            className="mt-1 border rounded px-2 py-1.5 text-sm w-64"
            value={contrato}
            onChange={(e) => setContrato(e.target.value)}
          >
            {contratos.map((c) => (
              <option key={c.id_contrato} value={c.id_contrato}>
                {c.folio} — {c.contraparte}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Fecha de operación</span>
          <input
            type="date"
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
        </label>
        <span className="text-xs text-slate-500 pb-2">
          Activación: PML ≥ strike
        </span>
        <button
          type="button"
          onClick={calcular}
          disabled={loading || !contrato}
          className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
        >
          <Calculator className="w-4 h-4" />
          {loading ? "Calculando…" : "Calcular"}
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>}

      {res && (
        <>
          {/* Estado / acciones del workflow */}
          <div
            className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${
              confirmado ? "bg-emerald-50 border-emerald-200" : "bg-slate-50"
            }`}
          >
            <div className="flex items-center gap-2 text-sm">
              {confirmado ? (
                <>
                  <Lock className="w-4 h-4 text-emerald-700" />
                  <span className="font-medium text-emerald-800">
                    {res.estado === "enviado" ? "Enviado a CENACE" : "Confirmado"}
                  </span>
                  <span className="text-slate-500">
                    {res.confirmado_por ? `· por ${res.confirmado_por}` : ""}
                    {res.confirmado_en ? ` · ${formatFecha(res.confirmado_en)}` : ""}
                    {" "}· bloqueado, no puede cambiar
                  </span>
                </>
              ) : (
                <span className="text-slate-600">
                  Cálculo en <b>borrador</b> (en vivo). Confírmalo para formalizarlo y bloquearlo.
                </span>
              )}
            </div>
            {has("tbfin") && (
            <div className="flex items-center gap-2">
              {res.estado === "borrador" && (
                <button
                  type="button"
                  onClick={confirmar}
                  disabled={accion}
                  title={res.horas_activas === 0
                    ? "Sin horas activas: confirmar lo deja como 'Sin Activación' (no hay TBFin que registrar)."
                    : ""}
                  className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded px-4 py-1.5 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                >
                  <Lock className="w-4 h-4" />{" "}
                  {accion ? "Confirmando…" : res.horas_activas === 0 ? "Confirmar (sin activación)" : "Confirmar"}
                </button>
              )}
              {confirmado && (
                <button
                  type="button"
                  onClick={descargar}
                  className="inline-flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm bg-white hover:bg-slate-50"
                >
                  <Download className="w-4 h-4" /> Descargar archivo
                </button>
              )}
              {res.estado === "confirmado" && (
                <>
                  {res.horas_activas === 0 ? (
                    <span className="text-xs text-slate-600 bg-slate-100 border rounded px-2.5 py-1.5">
                      <b>Sin activación</b>: ninguna hora con PML ≥ strike — no hay TBFin que registrar en CENACE.
                    </span>
                  ) : res.rol_mpp === "comprador" ? (
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                      MPP es comprador: la contraparte registra la TBFin y MPP la acepta. El ETRM no la envía a CENACE.
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={enviarCenace}
                      disabled={accion}
                      title="Envía y REGISTRA automáticamente en el SIM de CENACE (acción real y vinculante)."
                      className="inline-flex items-center gap-1.5 bg-blue-600 text-white rounded px-4 py-1.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" /> {accion ? "Procesando…" : "Enviar a CENACE"}
                    </button>
                  )}
                  {res.horas_activas > 0 && (
                    <button
                      type="button"
                      onClick={enviar}
                      disabled={accion}
                      title="Registro manual: marca el cálculo como enviado sin automatizar (si lo subiste tú al SIM)."
                      className="inline-flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm bg-white hover:bg-slate-50 disabled:opacity-50"
                    >
                      Marcar enviado
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={reabrir}
                    disabled={accion}
                    className="inline-flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm hover:bg-white disabled:opacity-50"
                  >
                    <LockOpen className="w-4 h-4" /> Reabrir
                  </button>
                </>
              )}
              {res.estado === "enviado" && has("cancelar_envio") && (
                <button
                  type="button"
                  onClick={cancelarEnvio}
                  disabled={accion}
                  title="Reabre el envío para poder reenviar. NO cancela el registro en CENACE."
                  className="inline-flex items-center gap-1.5 border border-red-300 text-red-700 rounded px-3 py-1.5 text-sm bg-white hover:bg-red-50 disabled:opacity-50"
                >
                  <LockOpen className="w-4 h-4" /> {accion ? "Cancelando…" : "Cancelar envío"}
                </button>
              )}
            </div>
            )}
          </div>

          {res.estado === "enviando" && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              Enviando a CENACE… el robot inicia sesión con tu FIEL, llena el SIM y registra.
              Puede tardar 1–2 min; esta pantalla se actualiza sola.
            </div>
          )}

          {res.advertencias.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm space-y-1">
              {res.advertencias.map((a, i) => <div key={i}>⚠ {a}</div>)}
            </div>
          )}

          {/* Estatus CENACE (folio + REG/ACE/TER) */}
          {(res.estado !== "borrador" || res.folio_cenace) && (
            <div className="bg-white border rounded-lg p-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <div>
                <div className="text-xs text-slate-400">Folio CENACE</div>
                {puedeEditarFolio && res.estado === "enviado" ? (
                  <div className="mt-0.5 flex items-center gap-2">
                    <input
                      type="text"
                      value={folioEdit}
                      onChange={(e) => setFolioEdit(e.target.value)}
                      placeholder="FM-…"
                      className="border rounded px-2 py-1 text-sm font-mono w-64"
                    />
                    <button
                      type="button"
                      onClick={guardarFolio}
                      disabled={accion || folioEdit.trim() === (res.folio_cenace ?? "")}
                      className="border rounded px-2.5 py-1 text-sm bg-white hover:bg-slate-50 disabled:opacity-50"
                    >
                      Guardar
                    </button>
                  </div>
                ) : puedeEditarFolio ? (
                  <div className="mt-0.5">
                    <div className="font-mono text-slate-800">{res.folio_cenace ?? "—"}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      El folio se puede capturar una vez enviado o marcado como enviado.
                    </div>
                  </div>
                ) : (
                  <div className="font-mono text-slate-800">
                    {res.folio_cenace ??
                      (res.estatus_cenace === "SIN_FOLIO"
                        ? "— (CENACE no emitió folio)"
                        : "— (pendiente de sincronizar)")}
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs text-slate-400">Estatus CENACE</div>
                {res.estatus_cenace === "ACE" ? (
                  <span className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-700">
                    ACE · Aceptada
                  </span>
                ) : res.estatus_cenace === "REG" ? (
                  <span className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-700">
                    REG · En proceso
                  </span>
                ) : res.estatus_cenace === "TER" ? (
                  <span className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-violet-100 text-violet-700">
                    TER · Terminada
                  </span>
                ) : res.estatus_cenace === "SIN_FOLIO" ? (
                  <span
                    className="inline-block rounded px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700"
                    title="El ETRM lo marcó enviado pero CENACE no emitió folio (o no se pudo emparejar). Verifica en el SIM y reenvía o captura el folio."
                  >
                    SIN FOLIO · no asignado
                  </span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </div>
              {has("tbfin") && (
                <div className="ml-auto flex items-center gap-3">
                  {avisoSync && <span className="text-xs text-emerald-600">{avisoSync}</span>}
                  <button
                    type="button"
                    onClick={sincronizarEstatus}
                    disabled={accion}
                    title="Lee Index (REG/ACE) e IndexTBFR (TER) del SIM y actualiza"
                    className="inline-flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm bg-white hover:bg-slate-50 disabled:opacity-50"
                  >
                    <RefreshCw className="w-4 h-4" /> Actualizar estatus CENACE
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Historial (bitácora de movimientos) */}
          <div className="bg-white border rounded-lg">
            <div className="px-4 py-2 border-b text-sm font-semibold text-slate-800 flex items-center justify-between">
              <span>Historial</span>
              {bitacoraQ.isFetching && <span className="text-xs text-slate-400">actualizando…</span>}
            </div>
            <div className="p-4">
              {bitacoraQ.isError ? (
                <div className="text-sm text-red-600">{(bitacoraQ.error as Error).message}</div>
              ) : (bitacoraQ.data?.length ?? 0) === 0 ? (
                <div className="text-sm text-slate-400">
                  Sin movimientos para este contrato y fecha.
                </div>
              ) : (
                <ul className="space-y-2.5">
                  {bitacoraQ.data!.map((e, i) => {
                    const lbl = EVENTO_LABEL[e.evento] ?? {
                      txt: e.evento,
                      cls: "bg-slate-100 text-slate-700",
                    };
                    return (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <span
                          className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${lbl.cls}`}
                        >
                          {lbl.txt}
                        </span>
                        <div className="min-w-0">
                          <div className="text-slate-700">
                            {e.usuario ?? "—"}
                            {e.detalle ? <span className="text-slate-500"> · {e.detalle}</span> : null}
                          </div>
                          <div className="text-xs text-slate-400">
                            {e.ts ? new Date(e.ts).toLocaleString("es-MX") : ""}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Fórmula + variables resueltas */}
          <div className="bg-white border rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-slate-800">
              Fórmula (vigente desde {res.insumos.vigente_desde})
            </h3>
            <div className="text-xs space-y-1">
              <div><span className="text-slate-400">strike =</span> <code className="font-mono">{res.insumos.formula_strike}</code></div>
              <div><span className="text-slate-400">mwh =</span> <code className="font-mono">{res.insumos.formula_mwh}</code></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm border-t pt-3">
              {res.insumos.variables.map((v) => (
                <Stat
                  key={v.nombre}
                  label={`${v.nombre} (${v.fuente})`}
                  value={`${n(v.valor, 4)}${v.detalle && v.fuente !== "constante" ? ` · ${v.detalle}` : ""}`}
                />
              ))}
              <Stat label="Strike" value={n(res.strike, 4)} strong />
              <Stat label="Nodo" value={res.nodo_pml} />
              <Stat label="Cuenta de orden (contraparte)" value={res.cuenta_orden ?? "—"} />
            </div>
          </div>

          {/* Resumen */}
          <div className="flex flex-wrap gap-4">
            <Card title="Horas activas" value={`${res.horas_activas} / 24`} />
            <Card title="MWh total del día" value={`${n(res.mwh_total, 3)} MWh`} />
            <Card title="Monto total del día" value={`$${n(res.monto_total)} MXN`} />
          </div>

          {/* Tabla 24 horas */}
          <div className="bg-white border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Hora</th>
                  <th className="px-3 py-2 font-medium text-right">PML (MXN/MWh)</th>
                  <th className="px-3 py-2 font-medium text-right">Strike (MXN/MWh)</th>
                  <th className="px-3 py-2 font-medium text-center">¿Activa?</th>
                  <th className="px-3 py-2 font-medium text-right">TBFin (MWh)</th>
                  <th className="px-3 py-2 font-medium text-right">Total (MXN)</th>
                </tr>
              </thead>
              <tbody>
                {res.horas.map((h) => (
                  <tr key={h.hora} className={`border-t ${h.activada ? "bg-emerald-50" : ""}`}>
                    <td className="px-3 py-1.5 tabular-nums">{h.hora}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{h.pml == null ? "—" : n(h.pml)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{n(res.strike)}</td>
                    <td className="px-3 py-1.5 text-center">
                      {h.activada ? (
                        <span className="text-emerald-700 font-medium">Sí</span>
                      ) : (
                        <span className="text-slate-400">No</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">{n(h.mwh, 3)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{h.total ? n(h.total) : "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t bg-slate-50 font-semibold">
                  <td className="px-3 py-2" colSpan={4}>Total del día</td>
                  <td className="px-3 py-2 text-right tabular-nums">{n(res.mwh_total, 3)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">${n(res.monto_total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className={`tabular-nums ${strong ? "font-semibold text-slate-900" : "text-slate-700"}`}>{value}</div>
    </div>
  );
}

function Card({ title, value }: { title: string; value: string }) {
  return (
    <div className="bg-white border rounded-lg px-4 py-3">
      <div className="text-xs text-slate-500">{title}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
